import { type QueryKey, useQueryClient } from "@tanstack/react-query";
import { USER_API, FEATURE_STATUS_RERUNNING, toastMsg } from "config";
import { normalizeId, handleToast, getApiErrorToastMessage } from "utils";
import {
  projectKeys,
  scriptKeys,
  characterKeys,
  coverageKeys,
  scriptAiPipelineLockKeys,
} from "queries";
import { sessionKeys } from "queries/profile.keys";
import { useAxios } from "../useAxios";
import { type ScopedRequest, useScopedMutation } from "../useScopedMutation";
import { useMemo } from "react";
import { isBreakdownAlreadyTaggedError, type AiRunMergePolicy } from "utils/aiRunMergePolicy";

const SCRIPT_BREAKDOWN = "script_breakdown";
const CHARACTER_BREAKDOWN = "character_breakdown";
const COVERAGE = "coverage";

type ProjectCache = Record<string, unknown> & {
  featureStatuses?: Array<{
    featureStatusId: number;
    feature: string;
    status: string;
    metadata: unknown;
  }>;
  feature_statuses?: Array<{
    feature_status_id: number;
    feature: string;
    status: string;
    metadata: unknown;
  }>;
};

function pickStatuses(old: ProjectCache): {
  listKey: "featureStatuses" | "feature_statuses";
  idKey: "featureStatusId" | "feature_status_id";
  rows: Array<{
    feature: string;
    status: string;
    metadata?: unknown;
    featureStatusId?: number;
    feature_status_id?: number;
  }>;
} {
  if (Array.isArray(old.featureStatuses)) {
    return { listKey: "featureStatuses", idKey: "featureStatusId", rows: old.featureStatuses };
  }
  if (Array.isArray(old.feature_statuses)) {
    return { listKey: "feature_statuses", idKey: "feature_status_id", rows: old.feature_statuses };
  }
  return { listKey: "featureStatuses", idKey: "featureStatusId", rows: [] };
}

function patchFeatureToRerunning(
  old: ProjectCache | undefined,
  featureSlug: string
): ProjectCache | undefined {
  if (!old) return old;
  const { listKey, idKey, rows } = pickStatuses(old);
  let next = rows.map((fs) => {
    if (fs.feature === featureSlug) {
      return { ...fs, status: FEATURE_STATUS_RERUNNING };
    }
    return fs;
  });
  const has = next.some((fs) => fs.feature === featureSlug);
  if (!has) {
    next = [
      ...next,
      {
        [idKey]: 0,
        feature: featureSlug,
        status: FEATURE_STATUS_RERUNNING,
        metadata: null,
      } as (typeof next)[0],
    ];
  }
  return {
    ...old,
    [listKey]: next,
  };
}

type AiRunScope = { pid: string; did: string };
type AiRunBody = { merge_policy?: AiRunMergePolicy };
type AiRunRequest = ScopedRequest<AiRunBody | undefined, AiRunScope>;
/** What a start changed in the cache, so a failed start can put it back where it was made. */
type AiRunStart = { previousProject: unknown; projectKey: QueryKey; lockKey: QueryKey };

export function useScriptAiRuns(projectId: string | number, documentId: string | number) {
  const queryClient = useQueryClient();
  const axios = useAxios({ allResponse: false });
  const pid = normalizeId(projectId);
  const did = normalizeId(documentId);

  const invalidateScriptBreakdownQueries = () => {
    queryClient.invalidateQueries({ queryKey: scriptKeys.scenes(pid, did) });
    queryClient.invalidateQueries({ queryKey: scriptKeys.draftScenes(pid, did) });
    queryClient.invalidateQueries({ queryKey: scriptKeys.draftSceneDocumentPrefix(pid, did) });
    queryClient.invalidateQueries({ queryKey: scriptKeys.tags(pid, did) });
    queryClient.invalidateQueries({ queryKey: scriptKeys.tagCategories(pid, did) });
    queryClient.invalidateQueries({ queryKey: scriptKeys.all });
  };

  const invalidateForFeature = (featureSlug: string) => {
    queryClient.invalidateQueries({ queryKey: projectKeys.detail(pid) });
    if (featureSlug === SCRIPT_BREAKDOWN) {
      invalidateScriptBreakdownQueries();
    }
    if (featureSlug === CHARACTER_BREAKDOWN) {
      queryClient.invalidateQueries({ queryKey: characterKeys.all });
    }
    if (featureSlug === COVERAGE) {
      queryClient.invalidateQueries({ queryKey: coverageKeys.detail(pid, did) });
    }
  };

  /**
   * The project and draft a start was made in. The Run AI hub stays mounted across a draft
   * switch, and React Query hands a start each new render's callbacks until it settles (also
   * while it waits offline), so the request, lock, rollback and refresh all read this from the
   * start's own request, never from the render.
   */
  const scope = useMemo<AiRunScope>(() => ({ pid, did }), [pid, did]);

  const handleMutationError = (error: unknown, started: AiRunStart | undefined) => {
    if (started?.previousProject) {
      queryClient.setQueryData(started.projectKey, started.previousProject);
    }
    // The Run AI hub explains this refusal itself and offers to replace the tags.
    if (isBreakdownAlreadyTaggedError(error)) return;
    handleToast(getApiErrorToastMessage(error, toastMsg.genericServerErr), "error");
  };

  /**
   * Locks the started draft's script and marks its project as re-running, and returns what a
   * failed start puts back.
   */
  const lockStartedDraft = async (target: AiRunScope, featureSlug: string): Promise<AiRunStart> => {
    const projectKey = projectKeys.detail(target.pid);
    const lockKey = scriptAiPipelineLockKeys.document(target.pid, target.did);
    await queryClient.cancelQueries({ queryKey: projectKey });
    const previousProject = queryClient.getQueryData(projectKey);
    queryClient.setQueryData(lockKey, true);
    queryClient.setQueriesData({ queryKey: projectKey }, (old: unknown) =>
      patchFeatureToRerunning(old as ProjectCache, featureSlug)
    );
    return { previousProject, projectKey, lockKey };
  };

  const unlockStartedDraft = (started: AiRunStart | undefined) => {
    if (started) queryClient.setQueryData(started.lockKey, false);
  };

  const refreshStartedProject = (target: AiRunScope) => {
    queryClient.invalidateQueries({ queryKey: projectKeys.detail(target.pid) });
  };

  const scriptMutation = useScopedMutation(scope, {
    mutationFn: async ({ variables, scope: target }: AiRunRequest) =>
      axios.post(USER_API.runScriptBreakdownAi(target.pid, target.did), variables ?? {}, {
        headers: { "Idempotency-Key": globalThis.crypto.randomUUID() },
      }),
    onMutate: ({ scope: target }) => lockStartedDraft(target, SCRIPT_BREAKDOWN),
    onSuccess: (_data, { scope: target }) => refreshStartedProject(target),
    onError: (error, _request, started) => {
      unlockStartedDraft(started);
      handleMutationError(error, started);
    },
  });

  const characterMutation = useScopedMutation(scope, {
    mutationFn: async ({ variables, scope: target }: AiRunRequest) =>
      axios.post(USER_API.runCharacterBreakdownAi(target.pid, target.did), variables ?? {}, {
        headers: { "Idempotency-Key": globalThis.crypto.randomUUID() },
      }),
    onMutate: ({ scope: target }) => lockStartedDraft(target, CHARACTER_BREAKDOWN),
    onSuccess: (_data, { scope: target }) => refreshStartedProject(target),
    onError: (error, _request, started) => {
      unlockStartedDraft(started);
      handleMutationError(error, started);
    },
  });

  const coverageMutation = useScopedMutation(scope, {
    mutationFn: async ({ variables, scope: target }: AiRunRequest) =>
      axios.post(USER_API.runCoverageAi(target.pid, target.did), variables ?? {}, {
        headers: { "Idempotency-Key": globalThis.crypto.randomUUID() },
      }),
    onMutate: ({ scope: target }) => lockStartedDraft(target, COVERAGE),
    onSuccess: (_data, { scope: target }) => refreshStartedProject(target),
    onError: (error, _request, started) => {
      unlockStartedDraft(started);
      handleMutationError(error, started);
    },
  });

  const runAllSequential = useMemo(() => {
    return async (
      order: Array<"script_breakdown" | "character_breakdown" | "coverage">,
      body: { merge_policy?: AiRunMergePolicy } = {}
    ) => {
      for (const step of order) {
        if (step === SCRIPT_BREAKDOWN) {
          await scriptMutation.mutateAsync(body);
        } else if (step === CHARACTER_BREAKDOWN) {
          await characterMutation.mutateAsync(body);
        } else {
          await coverageMutation.mutateAsync(body);
        }
      }
    };
  }, [scriptMutation, characterMutation, coverageMutation]);

  const isAnyPending =
    scriptMutation.isPending || characterMutation.isPending || coverageMutation.isPending;

  return {
    runScriptBreakdown: scriptMutation,
    runCharacterBreakdown: characterMutation,
    runCoverage: coverageMutation,
    runAllSequential,
    isAnyPending,
    invalidateForFeature,
    invalidateScriptBreakdownQueries,
    invalidateProfile: () => queryClient.invalidateQueries({ queryKey: sessionKeys.all }),
  };
}
